import { useState } from 'react';
import { Mascot } from '../effects/Mascot';
import { cx } from '../internal/cx';

export type AvatarProps = {
  src?: string;
  /**
   * Variante WebP (préférentielle, plus légère). Si fournie avec `src`, l'image
   * est rendue via `<picture>` : le NAVIGATEUR choisit le format (spec 07 §2.3),
   * `src` (PNG) restant le fallback universel.
   */
  webpSrc?: string;
  /**
   * Version animée (mp4). Si fournie avec `src`, la vidéo est jouée en boucle,
   * muette, PAR-DESSUS l'image — celle-ci sert de vignette le temps du chargement
   * et de fallback si la vidéo casse (spec 07 §2.3). Sans `src`, ignorée (mascotte).
   */
  videoSrc?: string;
  /** Nom affiché — sert d'`alt`/`aria-label`. */
  name?: string;
  size?: 'sm' | 'md' | 'lg' | number;
  className?: string;
};

const SIZES: Record<'sm' | 'md' | 'lg', number> = { sm: 32, md: 44, lg: 64 };

/**
 * Avatar rond. Sans image (ou si elle casse) → fallback mascotte Redpan.
 * Avec `videoSrc`, la version animée se superpose à l'image (vignette + fallback).
 */
export function Avatar({ src, webpSrc, videoSrc, name, size = 'md', className }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const px = typeof size === 'number' ? size : SIZES[size];
  const showImage = src && !broken;

  const img = showImage ? (
    <img
      src={src}
      alt={name ?? ''}
      width={px}
      height={px}
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  ) : null;

  // `contents` : le <picture> est neutre pour le layout, l'<img> garde object-cover.
  const picture = img
    ? webpSrc
      ? (
          <picture className="contents">
            <source srcSet={webpSrc} type="image/webp" />
            {img}
          </picture>
        )
      : img
    : null;

  const showVideo = Boolean(picture && videoSrc && !videoBroken);

  return (
    <span
      role={!showImage && name ? 'img' : undefined}
      aria-label={!showImage && name ? name : undefined}
      className={cx(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'border border-ink-600/60 bg-ink-700',
        className,
      )}
      style={{ width: px, height: px }}
    >
      {picture ? (
        <>
          {picture}
          {showVideo && (
            <video
              src={videoSrc}
              autoPlay
              muted
              loop
              playsInline
              // La vignette (image) est déjà rendue dessous : pas besoin de poster.
              preload="metadata"
              aria-hidden="true"
              tabIndex={-1}
              onError={() => setVideoBroken(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
        </>
      ) : (
        <Mascot animated={false} size={px * 0.86} className="translate-y-[8%]" />
      )}
    </span>
  );
}
